import { IEntity } from "domain/Domain/Entities/IEntity";
import { useRequest } from "hooks/useRequest";
import { MaintenanceResult } from "../domain/models/MaintenanceResult";

export const useSystemContextRequester = () => {
	const request = useRequest("systemContext");

	return {
		...request,
		filterSearch: async ({
			products,
			categories,
			stores,
			tags,
		}: {
			products: IEntity[];
			categories: IEntity[];
			stores: IEntity[];
			tags?: IEntity[];
		}) => {
			return await request
				.POST("FilterSearch", {
					products,
					categories,
					stores,
					tags,
				})
				.then((c) => {
					const result = c.data as {
						products: IEntity[];
						categories: IEntity[];
						stores: IEntity[];
						tags?: IEntity[];
					};

					return transformFilters(result);
				});
		},
		systemSearch: (text: string, onlyActive: boolean) => {
			return request
				.GET(
					`systemSearch?searchString=${text}&onlyActive=${onlyActive}`,
				)
				.then((c) => {
					const result = c.data as {
						products: IEntity[];
						categories: IEntity[];
						stores: IEntity[];
						tags?: IEntity[];
					};

					return transformFilters(result);
				});
		},
		status: () => {
			return request.GET("status").then(
				(c) =>
					c.data as {
						result: MaintenanceResult;
						latestOrder: string;
						lastDataBaseUpdate: string;
						onMaintenance: boolean;
					},
			);
		},
	};
};

export const transformFilters = (filter: {
	products: IEntity[];
	stores: IEntity[];
	categories: IEntity[];
	tags?: IEntity[];
}) => {
	const products = filter.products?.map((p: any) => ({
		...p,
		icon: "package",
		color: "primary",
		type: "product",
	})) || [];

	const categories = filter.categories?.map((p: any) => ({
		...p,
		icon: "bookmark",
		color: "secondary",
		type: "category",
	})) || [];

	const stores = filter.stores?.map((p: any) => ({
		...p,
		icon: "home",
		color: "darkPurple",
		type: "store",
	})) || [];

	const extractTagNames = (items: any[]): string[] =>
		items.flatMap((item) =>
			item.tagsText
				? item.tagsText.split("|").map((t: string) => t.trim()).filter(Boolean)
				: []
		);

	const tagNamesFromItems = [
		...extractTagNames(filter.products || []),
		...extractTagNames(filter.stores || []),
	];

	const topLevelTagNames = filter.tags?.map((t: any) => t.name).filter(Boolean) ?? [];

	const uniqueTagNames = Array.from(new Set([...tagNamesFromItems, ...topLevelTagNames]));

	const tags = uniqueTagNames.map((name) => ({
		name,
		id: name,
		icon: "flag",
		color: "info",
		type: "tag",
	}));

	return {
		products,
		categories,
		stores,
		tags,
		items: [...products, ...categories, ...stores, ...tags],
	};
};
